import { faEdit, faTrash } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    BoxProps,
    Chip,
    IconButton,
    Box as MuiBox,
    Button as MuiButton,
    styled,
    Typography,
} from "@mui/material";
import {
    GridColDef,
    GridRowParams,
    DataGrid as MuiDataGrid,
} from "@mui/x-data-grid";
import dayjs from "dayjs";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { useGetAllMaps } from "src/api/map";
import { DEFAULT_PAGE_SIZE } from "src/components/ListView";
import { SettingContext, SettingContextProvider } from "src/contexts/settings";
import CreateMap from "src/dialogs/CreateMap";
import DeleteDialog from "src/dialogs/DeleteElement";
import { Enum_Entity } from "src/types";
import { Type_Map_Formatted } from "src/types/map";

const DataGrid = styled(MuiDataGrid)(({ theme }) => ({
    width: "98%",
    border: "none",
    marginTop: theme.spacing(2),
    "& .MuiDataGrid-cell": {
        display: "flex",
        alignItems: "center",
    },
}));

const Button = styled(MuiButton)(({ theme }) => ({
    color: theme.palette.secondary.dark,
    borderColor: theme.palette.secondary.dark,
    "&:hover": {
        color: theme.palette.secondary.dark,
        borderColor: theme.palette.secondary.dark,
    },
}));

const Box = styled(MuiBox)<BoxProps>(({ theme }) => ({
    marginTop: theme.spacing(4),
    marginLeft: theme.spacing(3),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
}));

const MapsView = () => {
    return (
        <SettingContextProvider>
            <MapSettings />
        </SettingContextProvider>
    );
};

const MapSettings = () => {
    const { updateMap } = useContext(SettingContext);
    const [openCreateMap, setOpenCreateMap] = useState(false);
    const [openDeleteMap, setOpenDeleteMap] = useState(false);

    const { data } = useGetAllMaps();

    const { t } = useTranslation();

    const handleEdit = (params: GridRowParams) => {
        updateMap(params as unknown as Type_Map_Formatted);
        setOpenCreateMap(true);
    };

    const handleDelete = (params: GridRowParams) => {
        updateMap(params as unknown as Type_Map_Formatted);
        setOpenDeleteMap(true);
    };

    const columns: GridColDef[] = [
        {
            field: "label",
            type: "string",
            flex: 2,
            renderCell: (params) => (
                <Typography>{params.value || ""}</Typography>
            ),
            renderHeader: () => <strong>{t("maps.label")}</strong>,
        },
        {
            field: "type",
            type: "string",
            flex: 2,
            renderCell: (params) => (
                <Chip label={t(`maps.${params.value}`)} variant="outlined" />
            ),
            renderHeader: () => <strong>{t("maps.type")}</strong>,
        },
        {
            field: "minZoom",
            type: "string",
            flex: 1,
            renderCell: (params) => (
                <Typography>{params.value.toString() || ""}</Typography>
            ),
            renderHeader: () => <strong>{t("maps.min_zoom")}</strong>,
        },
        {
            field: "maxZoom",
            type: "string",
            flex: 1,
            renderCell: (params) => (
                <Typography>{params.value.toString() || ""}</Typography>
            ),
            renderHeader: () => <strong>{t("maps.max_zoom")}</strong>,
        },
        {
            field: "startDate",
            type: "date",
            flex: 1.5,
            valueGetter: (params) => {
                const dayjsDate = dayjs(params);
                return dayjsDate.isValid() ? dayjsDate.toDate() : null;
            },
            renderCell: (params) => (
                <strong>{dayjs(params.value).format("L LT")}</strong>
            ),
            renderHeader: () => <strong>{t("maps.start_date")}</strong>,
        },
        {
            field: "endDate",
            type: "date",
            flex: 1.5,
            valueGetter: (params) => {
                const dayjsDate = dayjs(params);
                return dayjsDate.isValid() ? dayjsDate.toDate() : null;
            },
            renderCell: (params) => (
                <strong>
                    {" "}
                    {dayjs(params.value).isValid()
                        ? dayjs(params.value).format("L LT")
                        : ""}
                </strong>
            ),
            renderHeader: () => <strong>{t("maps.end_date")}</strong>,
        },
        {
            field: "actions",
            type: "actions",
            flex: 1,
            renderCell: (params) => (
                <>
                    <IconButton
                        size="small"
                        onClick={() => handleEdit(params.row)}
                    >
                        <FontAwesomeIcon icon={faEdit} />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => handleDelete(params.row)}
                    >
                        <FontAwesomeIcon icon={faTrash} />
                    </IconButton>
                </>
            ),
            renderHeader: () => <strong>{t("actions")}</strong>,
        },
    ];

    return (
        <MuiBox p={2}>
            <Box>
                <Typography variant="h5">{t("maps.list")}</Typography>
                <Button
                    variant="outlined"
                    onClick={() => {
                        updateMap(null);
                        setOpenCreateMap(true);
                    }}
                >
                    {t("maps.create_map")}
                </Button>
            </Box>
            <DataGrid
                rows={data}
                columns={columns}
                initialState={{
                    pagination: {
                        paginationModel: {
                            pageSize: DEFAULT_PAGE_SIZE,
                        },
                    },
                }}
                pageSizeOptions={[5, DEFAULT_PAGE_SIZE, 15]}
                disableColumnMenu
            />

            {openCreateMap && (
                <CreateMap close={() => setOpenCreateMap(false)} />
            )}

            {openDeleteMap && (
                <DeleteDialog
                    closeDeleteDialog={() => setOpenDeleteMap(false)}
                    entity={Enum_Entity.MAP}
                />
            )}
        </MuiBox>
    );
};

export default MapsView;
